import { reportError } from '../monitoring.js';
import { APP_NAME, APP_MARK } from '../brand.js';

const DISCLAIMER =
  'Independent practice simulator. It is not affiliated with or endorsed by any employer, assessment provider or financial institution, and does not reproduce proprietary questions.';

// These are routine user-input outcomes, not system failures — don't page the owner for them.
const EXPECTED_AUTH_ERROR_PATTERNS = [
  /invalid login credentials/i,
  /email not confirmed/i,
  /user already registered/i,
  /password should be at least/i,
];

function reportIfUnexpected(err, context) {
  if (EXPECTED_AUTH_ERROR_PATTERNS.some((pattern) => pattern.test(err?.message || ''))) return;
  reportError(err, context);
}

function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
}

function friendlyAuthError(err) {
  const message = err?.message || String(err || 'Something went wrong.');
  if (/invalid login credentials/i.test(message)) return 'Incorrect email or password.';
  if (/email not confirmed/i.test(message)) return 'Please verify your email before signing in — check your inbox for the confirmation link.';
  if (/user already registered/i.test(message)) return 'An account with that email already exists — try signing in instead.';
  if (/password should be at least/i.test(message)) return message;
  return message;
}

export function createAuthScreen({ container, supabase, onAuthenticated }) {
  let view = 'welcome';
  let error = '';
  let notice = '';
  let loading = false;

  function shell(inner) {
    return `
      <div class="auth-shell">
        <div class="auth-card">
          <div class="auth-brand">
            <span class="auth-brand-mark">${APP_MARK}</span>
            <span class="auth-brand-name">${APP_NAME.toUpperCase()}</span>
          </div>
          ${error ? `<p class="auth-error">${escapeHTML(error)}</p>` : ''}
          ${notice ? `<p class="auth-notice">${escapeHTML(notice)}</p>` : ''}
          ${inner}
          <p class="auth-disclaimer">${escapeHTML(DISCLAIMER)}</p>
        </div>
      </div>
    `;
  }

  function renderWelcome() {
    return shell(`
      <h1 class="auth-title">Welcome to ${APP_NAME}</h1>
      <p class="auth-copy">A focused practice platform for quantitative, trading and finance recruitment assessments.</p>
      <div class="auth-actions">
        <button class="auth-button" data-action="go-signin">Sign in</button>
        <button class="auth-button auth-button-ghost" data-action="go-signup">Create account</button>
      </div>
    `);
  }

  function renderSignIn() {
    return shell(`
      <h1 class="auth-title">Sign in</h1>
      <p class="auth-copy">Welcome back — your practice history and progress are waiting.</p>
      <form data-form="signin">
        <div class="auth-field">
          <label for="signin-email">Email address</label>
          <input id="signin-email" name="email" type="email" autocomplete="email" required />
        </div>
        <div class="auth-field">
          <label for="signin-password">Password</label>
          <input id="signin-password" name="password" type="password" autocomplete="current-password" required />
        </div>
        <div class="auth-actions">
          <button class="auth-button" type="submit" ${loading ? 'disabled' : ''}>${loading ? 'Signing in…' : 'Sign in'}</button>
        </div>
      </form>
      <div class="auth-link-row">
        <button data-action="go-reset">Forgot password?</button>
        <button data-action="go-signup">Create account</button>
      </div>
    `);
  }

  function renderSignUp() {
    return shell(`
      <h1 class="auth-title">Create your account</h1>
      <p class="auth-copy">Your completed test history and progress will be saved to this account.</p>
      <form data-form="signup">
        <div class="auth-field">
          <label for="signup-name">Display name</label>
          <input id="signup-name" name="displayName" type="text" autocomplete="name" required maxlength="80" />
        </div>
        <div class="auth-field">
          <label for="signup-email">Email address</label>
          <input id="signup-email" name="email" type="email" autocomplete="email" required />
        </div>
        <div class="auth-field">
          <label for="signup-password">Password</label>
          <input id="signup-password" name="password" type="password" autocomplete="new-password" required minlength="8" />
        </div>
        <div class="auth-field">
          <label for="signup-confirm">Confirm password</label>
          <input id="signup-confirm" name="confirmPassword" type="password" autocomplete="new-password" required minlength="8" />
        </div>
        <div class="auth-actions">
          <button class="auth-button" type="submit" ${loading ? 'disabled' : ''}>${loading ? 'Creating account…' : 'Create account'}</button>
        </div>
      </form>
      <div class="auth-link-row">
        <button data-action="go-signin">Already have an account? Sign in</button>
      </div>
    `);
  }

  function renderCheckEmail() {
    return shell(`
      <h1 class="auth-title">Check your email</h1>
      <p class="auth-copy">We've sent a verification link to confirm your address. Once verified, sign in to open your dashboard.</p>
      <div class="auth-actions">
        <button class="auth-button" data-action="go-signin">Back to sign in</button>
      </div>
    `);
  }

  function renderReset() {
    return shell(`
      <h1 class="auth-title">Reset your password</h1>
      <p class="auth-copy">Enter your email and we'll send you a link to choose a new password.</p>
      <form data-form="reset">
        <div class="auth-field">
          <label for="reset-email">Email address</label>
          <input id="reset-email" name="email" type="email" autocomplete="email" required />
        </div>
        <div class="auth-actions">
          <button class="auth-button" type="submit" ${loading ? 'disabled' : ''}>${loading ? 'Sending…' : 'Send reset link'}</button>
        </div>
      </form>
      <div class="auth-link-row">
        <button data-action="go-signin">Back to sign in</button>
      </div>
    `);
  }

  function renderResetSent() {
    return shell(`
      <h1 class="auth-title">Reset link sent</h1>
      <p class="auth-copy">Check your email for a link to choose a new password.</p>
      <div class="auth-actions">
        <button class="auth-button" data-action="go-signin">Back to sign in</button>
      </div>
    `);
  }

  function renderSetNewPassword() {
    return shell(`
      <h1 class="auth-title">Choose a new password</h1>
      <p class="auth-copy">You're verified — set a new password to finish resetting your account.</p>
      <form data-form="set-new-password">
        <div class="auth-field">
          <label for="new-password">New password</label>
          <input id="new-password" name="password" type="password" autocomplete="new-password" required minlength="8" />
        </div>
        <div class="auth-field">
          <label for="new-password-confirm">Confirm new password</label>
          <input id="new-password-confirm" name="confirmPassword" type="password" autocomplete="new-password" required minlength="8" />
        </div>
        <div class="auth-actions">
          <button class="auth-button" type="submit" ${loading ? 'disabled' : ''}>${loading ? 'Saving…' : 'Save new password'}</button>
        </div>
      </form>
    `);
  }

  function render() {
    const views = {
      welcome: renderWelcome,
      signin: renderSignIn,
      signup: renderSignUp,
      'check-email': renderCheckEmail,
      reset: renderReset,
      'reset-sent': renderResetSent,
      'set-new-password': renderSetNewPassword,
    };
    container.innerHTML = (views[view] || renderWelcome)();
  }

  function setView(next) {
    view = next;
    error = '';
    notice = '';
    render();
  }

  async function handleSignIn(form) {
    error = '';
    loading = true;
    render();
    const email = form.email.value.trim();
    const password = form.password.value;
    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    loading = false;
    if (signInError) {
      reportIfUnexpected(signInError, { flow: 'sign-in' });
      error = friendlyAuthError(signInError);
      render();
      return;
    }
    if (data?.session) onAuthenticated(data.session);
  }

  async function handleSignUp(form) {
    error = '';
    const displayName = form.displayName.value.trim();
    const email = form.email.value.trim();
    const password = form.password.value;
    const confirmPassword = form.confirmPassword.value;
    if (password !== confirmPassword) {
      error = 'Passwords do not match.';
      render();
      return;
    }
    loading = true;
    render();
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: window.location.origin,
      },
    });
    loading = false;
    if (signUpError) {
      reportIfUnexpected(signUpError, { flow: 'sign-up' });
      error = friendlyAuthError(signUpError);
      render();
      return;
    }
    if (data?.session) {
      onAuthenticated(data.session);
      return;
    }
    setView('check-email');
  }

  async function handleReset(form) {
    error = '';
    loading = true;
    render();
    const email = form.email.value.trim();
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    loading = false;
    if (resetError) {
      reportIfUnexpected(resetError, { flow: 'reset-request' });
      error = friendlyAuthError(resetError);
      render();
      return;
    }
    setView('reset-sent');
  }

  async function handleSetNewPassword(form) {
    error = '';
    const password = form.password.value;
    const confirmPassword = form.confirmPassword.value;
    if (password !== confirmPassword) {
      error = 'Passwords do not match.';
      render();
      return;
    }
    loading = true;
    render();
    const { error: updateError } = await supabase.auth.updateUser({ password });
    loading = false;
    if (updateError) {
      reportIfUnexpected(updateError, { flow: 'set-new-password' });
      error = friendlyAuthError(updateError);
      render();
      return;
    }
    const { data } = await supabase.auth.getSession();
    if (data?.session) onAuthenticated(data.session);
  }

  container.addEventListener('click', (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;
    const action = button.dataset.action;
    if (action === 'go-signin') setView('signin');
    else if (action === 'go-signup') setView('signup');
    else if (action === 'go-reset') setView('reset');
  });

  container.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    if (!form) return;
    event.preventDefault();
    const kind = form.dataset.form;
    if (kind === 'signin') handleSignIn(form);
    else if (kind === 'signup') handleSignUp(form);
    else if (kind === 'reset') handleReset(form);
    else if (kind === 'set-new-password') handleSetNewPassword(form);
  });

  return {
    render,
    showPasswordRecovery() { setView('set-new-password'); },
    showExpiredSessionNotice() {
      view = 'signin';
      error = '';
      notice = 'Your session expired — please sign in again.';
      render();
    },
  };
}
